import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react'
import { MaskLines, Reveal } from '@/components/motion/reveal'
import { ToneSection } from '@/components/generals/tone'
import { backers } from '@/constants/content'

const BackerName: FC<{ name: string, role: string, index: number }> = ({ name, role, index }): ReactNode => {
    const ref = useRef<HTMLLIElement>(null)
    const reduce = useReducedMotion()
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.9', 'start 0.35'] })
    const fill = useTransform(scrollYProgress, [0, 1], reduce ? ['inset(0 0% 0 0)', 'inset(0 0% 0 0)'] : ['inset(0 100% 0 0)', 'inset(0 0% 0 0)'])

    return (
        <li ref={ref} className='grid gap-3 border-t border-tone-line pt-5 md:grid-cols-12 md:items-end'>
            <span className='t-label text-tone-muted md:col-span-2'>
                0{index + 1}
            </span>

            <span className='relative block font-heading text-[clamp(3.5rem,13vw,13rem)] font-semibold uppercase leading-[0.82] tracking-[-0.045em] wdth-112 md:col-span-8'>
                <span className='text-outline text-tone/35' aria-hidden>
                    {name}
                </span>
                <motion.span className='absolute inset-0' style={{ clipPath: fill }}>
                    {name}
                </motion.span>
            </span>

            <span className='t-label text-tone-accent md:col-span-2 md:pb-4 md:text-right'>
                {role}
            </span>
        </li>
    )
}

export const Insured: FC = (): ReactNode => {
    return (
        <ToneSection tone='void' id='who-insures' className='px-5 py-32 md:px-8 lg:px-12 lg:py-48'>
            <div className='grid gap-14 lg:grid-cols-12'>
                <div className='lg:col-span-7'>
                    <span className='t-label text-tone-muted'>
                        (04) Backing
                    </span>
                    <MaskLines
                        as='h2'
                        className='mt-8 font-heading t-display font-semibold uppercase wdth-75'
                        lines={[
                            'Who insures',
                            <span key='nsure' className='normal-case'>
                                nSure.ai<span className='font-serif font-normal italic text-tone-accent'>?</span>
                            </span>
                        ]}
                    />
                </div>

                <Reveal className='flex flex-col gap-5 text-[1.0625rem] leading-relaxed text-tone-muted lg:col-span-4 lg:col-start-9 lg:pt-24'>
                    <p>
                        Payment fraud prevention solutions assume their clients’ liability while simultaneously betting on their own success. We, too, believe in our product, taking the risk of digital gift-card fraud off our clients’ shoulders, but we’re not the only ones betting on our success.
                    </p>
                    <p>
                        nSure.ai’s payment fraud prevention solution is backed by Phoenix and Kamet, while Lloyds has joined them with a stop-loss policy. This backing protects customers against catastrophic failure in the form of an undetected systemic payment fraud attack.
                    </p>
                    <p className='text-tone'>
                        By combining a well-maintained risk-minimizing algorithm with the confidence of a world-leading insurer, nSure.ai can assume liabilities at any scale.
                    </p>
                </Reveal>
            </div>

            <ul className='mt-24 flex flex-col gap-6 lg:mt-36'>
                {backers.map((backer, index) => (
                    <BackerName key={backer.name} name={backer.name} role={backer.role} index={index} />
                ))}
            </ul>
        </ToneSection>
    )
}
